# Querying a job ad

## Operations

### positionProfile

The [`positionProfile` query] returns the current state of a job ad.
In addition to the fields you provided during posting, it returns the URL of a live job ad and the end date of the posting.

Posting job ads to the SEEK job board is an asynchronous process.
Until the job ad has gone live the `positionProfile` query will return `null`.
The [`PositionProfilePosted` event] can be used to trigger a query of the position profile once it’s available,
and the `PositionProfileUpdated` event can be used for trigger behaviour on subsequent updates to it.

You can determine if a job ad is closed based on its [`postingInstructions.end` field].
If the posting end date is in the past then the job ad was closed on the specified date.
The [`PositionProfileClosed` event] is emitted when a job ad has been closed.

The [`PositionProfile`] object will remain for 180 days past its close date, after which the `positionProfile` query will return `null`.
Your software must store any data it needs to access after the 180 day period.

```graphql
query ($id: String!) {
  positionProfile(id: $id) {
    profileId {
      value
    }
    positionTitle
    positionUri
    postingInstructions {
      start
      end
    }
    jobCategories {
      id {
        value
      }
      name
    }
    positionLocation {
      id {
        value
      }
      contextualName
    }
    positionFormattedDescriptions {
      descriptionId {
        value
      }
      content
    }
    offeredRemunerationPackage {
      ranges {
        minimumAmount {
          value
          currency
        }
        maximumAmount {
          value
          currency
        }
        intervalCode
      }
    }
    seekAnzWorkTypeCode
  }
}
```

```json
{
  "id": "seekAnzPublicTest:positionProfile:jobAd:27cuZeA47"
}
```

```json
{
  "positionProfile": {
    "profileId": {
      "value": "seekAnzPublicTest:positionProfile:jobAd:27cuZeA47"
    },
    "positionTitle": "Customer Mobility Director",
    "positionUri": "https://au.candidate.example.seek.com/job/30000001",
    "postingInstructions": [
      {
        "start": "2019-08-25T21:24:16.735Z",
        "end": "2038-01-01T00:00:00.000Z"
      }
    ],
    "jobCategories": [
      {
        "id": {
          "value": "seekAnzPublicTest:jobCategory:seek:2i9B5dyLs"
        },
        "name": "Management & Support"
      }
    ],
    "positionLocation": [
      {
        "id": {
          "value": "seekAnzPublicTest:location:seek:23V5B6Tif"
        },
        "contextualName": "Mount Maunganui Bay of Plenty NZ"
      }
    ],
    "positionFormattedDescriptions": [
      {
        "descriptionId": {
          "value": "SearchSummary"
        },
        "content": "Exciting new director role in Auckland"
      },
      {
        "descriptionId": {
          "value": "AdvertisementDetails"
        },
        "content": "\n<strong>About the role</strong>\n<p>Ut necessitatibus amet deserunt laudantium veritatis. Sapiente occaecati\nodio quae quod magnam autem temporibus error rerum. Id porro distinctio eaque\neos earum sed incidunt fugiat. Minus quis iusto enim quod a. Autem sequi\nreiciendis quibusdam et nostrum.</p>\n<strong>Benefits and perks</strong>\n<p>Possimus libero sit vitae et sed quidem. Eos eveniet quo est id iste sint.\nNihil corporis in eos consequatur quas ut harum voluptates et. Deleniti minus\nvoluptatem maiores consequatur est molestiae quo est cupiditate. Mollitia\ndoloremque quam non. Omnis eum nihil nobis illo cum.</p>\n<strong>Skills and experience</strong>\n<p>Dolor quia beatae nulla perferendis culpa ea saepe ut. Excepturi non vitae\nullam consequuntur accusantium sit iste asperiores. Porro quisquam dolor\nquia. Natus ut sunt animi. Voluptatem minima perspiciatis maiores ut. Atque\nad qui sed beatae odit non.</p>\n"
      },
      {
        "descriptionId": {
          "value": "SearchBulletPoint"
        },
        "content": "Market leader in customer support"
      },
      {
        "descriptionId": {
          "value": "SearchBulletPoint"
        },
        "content": "Great work life balance in a beautiful location"
      }
    ],
    "offeredRemunerationPackage": {
      "ranges": [
        {
          "minimumAmount": {
            "value": 150000,
            "currency": "NZD"
          },
          "maximumAmount": {
            "value": 175000,
            "currency": "NZD"
          },
          "intervalCode": "Year"
        }
      ]
    },
    "seekAnzWorkTypeCode": "FullTime"
  }
}
```

[`positionprofile`]: /schema/named-type/PositionProfile
[`positionprofile` query]: /schema/query/positionProfile
[`positionprofileclosed` event]: /use-cases/job-posting/managing-job-ads/closing-a-job-ad#positionprofileclosed
[`positionprofileposted` event]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#positionprofileposted
[`postinginstructions.end` field]: /schema/named-type/PostingInstruction#/field/end
